import { useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { autocompleteTravellers } from '../api/endpoints/traveller';
import type { Traveller } from '../api/types/traveller';

function optionLabel(o: Traveller): string {
  const name = o.fullName?.trim() || [o.firstName, o.lastName].filter(Boolean).join(' ');
  const id = o.identity || o.passport;
  return id ? `${name} — ${id}` : name || '—';
}

/**
 * "Kayıtlı Yolcu Ara" type-ahead backed by `Traveller/Autocomplete` (min 3 chars,
 * debounced via staleTime) — matches on name OR T.C. kimlik / passport, same
 * search-as-you-type pattern as `PersonnelAutocomplete`.
 *
 * This is a pick-to-fill action, not a bound field: it holds no selection — on pick it
 * fires `onSelect(traveller)` and clears itself, leaving the caller to copy the values
 * into its own form.
 */
export function TravellerAutocomplete({
  label = 'Kayıtlı Yolcu Ara (ad veya T.C. kimlik)',
  onSelect,
  size = 'small',
}: {
  label?: string;
  onSelect: (traveller: Traveller) => void;
  size?: 'small' | 'medium';
}) {
  const [inputValue, setInputValue] = useState('');
  const [selected, setSelected] = useState<Traveller | null>(null);
  const trimmed = inputValue.trim();

  const { data: options = [], isFetching } = useQuery<Traveller[]>({
    queryKey: ['travellers', 'autocomplete', trimmed],
    queryFn: () => autocompleteTravellers(trimmed).then((r) => r.travellers ?? []),
    enabled: trimmed.length >= 3,
    staleTime: 60_000,
  });

  return (
    <Autocomplete<Traveller>
      fullWidth
      value={selected}
      blurOnSelect
      clearOnBlur
      onChange={(_, newValue) => {
        if (newValue) onSelect(newValue);
        setSelected(null);
        setInputValue('');
      }}
      inputValue={inputValue}
      // ignore MUI's post-select "reset" (it would drop the picked option's label into
      // the box) — this is a search field that never keeps a value
      onInputChange={(_, v, reason) => {
        if (reason !== 'reset') setInputValue(v);
      }}
      options={options}
      filterOptions={(x) => x}
      getOptionLabel={optionLabel}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      loading={isFetching}
      noOptionsText={
        trimmed.length < 3 ? 'Aramak için en az 3 karakter girin' : 'Kayıtlı yolcu bulunamadı'
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size={size}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {isFetching && <CircularProgress color="inherit" size={16} />}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
